import React, { useEffect, useRef } from 'react';
import { Alert, Button, Spin } from 'antd';

import { RATE_LIMITED_TEXT } from '../../utils/feedMessages';

/**
 * The foot of a list that pages itself in as it is scrolled: the sentinel the
 * next page is taken at, and the three things that can stand in its place.
 * Render it right after the list's rows.
 *
 *   <ListScrollLoader
 *     hasMore={hasMore} loadMore={loadMore} loadingMore={loadingMore}
 *     landedPages={landedPages} error={loadError} onRetry={retry}
 *     nothingToFill={…} suspended={…} rearmKey={viewMode}
 *   />
 *
 * - The sentinel loads the next page when it nears the viewport (600 px ahead).
 * - `nothingToFill`: every group folded away leaves nothing on screen that MORE
 *   items could fill, so the sentinel would sit in view and page through the
 *   whole collection to draw headers and nothing else. The auto-loader stands
 *   down and a plain Load more button takes its place - one page per click.
 * - A page that failed (`error`, the feed's `loadError`) stops the feed where
 *   the sentinel sat, and a row says so with its Retry beside it.
 * - `suspended`: the caller draws something else instead of the rows (a blocked
 *   worklist), so the feed's `hasMore` speaks for rows that are not on screen.
 *
 * The observer re-arms only when the LIST changes - a page landed
 * (`landedPages`: every page, a new feed's first included, whatever its size),
 * `hasMore` flipped, `rearmKey` changed (the view switched), a fold opened the
 * list up or shut it - never because a request finished. A new observer fires
 * at once on a sentinel already inside its margin, so re-arming on "loading
 * finished" turned one failed page into a request per frame (2026-09-27); and
 * an observer that fired while a page was in flight, and was refused, reports
 * nothing more unless the landing re-arms it.
 */
export default function ListScrollLoader({
	hasMore,
	loadMore,
	loadingMore = false,
	landedPages,
	error = null,
	onRetry,
	nothingToFill = false,
	suspended = false,
	rearmKey,
}) {
	const feedStopped = Boolean(error);
	const sentinelRef = useRef(null);
	useEffect(() => {
		const el = sentinelRef.current;
		if (!el || !hasMore || nothingToFill || feedStopped || suspended || typeof IntersectionObserver === 'undefined') return undefined;
		const observer = new IntersectionObserver(entries => {
			if (entries.some(entry => entry.isIntersecting)) loadMore();
		}, { rootMargin: '600px' });
		observer.observe(el);
		return () => observer.disconnect();
	}, [hasMore, loadMore, rearmKey, nothingToFill, feedStopped, suspended, landedPages]);

	const firstPageFailed = error?.page === 0;
	return (
		<>
			{hasMore && !nothingToFill && !suspended && !feedStopped ? <div ref={sentinelRef} className="items-page__sentinel" /> : null}
			{/* Folded flat: the headers, then the one door to more. */}
			{hasMore && nothingToFill && !suspended && !feedStopped ? (
				<div className="items-page__load-more">
					<Button loading={loadingMore} onClick={() => loadMore()}>Load more</Button>
				</div>
			) : null}
			{/* A page that failed stops the feed where the sentinel sat.
			    Retry asks for that same page again. */}
			{feedStopped && !suspended ? (
				<div className="items-page__feed-error" data-testid="items-feed-error">
					<Alert
						type="warning"
						showIcon
						message={error.kind === 'rate_limited'
							? RATE_LIMITED_TEXT
							: (firstPageFailed ? 'Couldn\'t load items.' : 'Couldn\'t load more items.')}
						action={<Button size="small" onClick={onRetry}>Retry</Button>}
					/>
				</div>
			) : null}
			{loadingMore && !nothingToFill ? (
				<div className="items-page__loading-more"><Spin size="small" /></div>
			) : null}
		</>
	);
}
